import { useQuery } from "@apollo/client";
import { Navigate, Route, Routes } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";

import ConfigManagement from "../ConfigManagement";
import IacManagementResourceDetails from "../IacManagement/ResourceDetails";
import ScheduleScanWizard from "../NewScanSchedule";
import Scans from "../Scans";
import ScansLayout from "../Scans/Layout";
import ScanDetailLayout from "../Scans/ScanDetail/Layout";
import ScanDetailScans from "../Scans/ScanDetail/Scans";
import ScanResources from "../Scans/ScanResources";
import ScanRunLayout from "../Scans/ScanRun/Layout";
import { GET_ACCOUNT_STACKS } from "./gql";
import ResourcesIacManagement from "./IacManagement";

const ResourcesWrapper = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { unmanagedResourcesUi } = useTypedFlags();

  useTitle(`Resources · ${accountName}`);

  const { error, loading, data } = useQuery<{
    hasAnsibleStacks?: boolean;
  }>(GET_ACCOUNT_STACKS);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  const configurationManagementEnabled = !!data?.hasAnsibleStacks;

  return (
    <Routes>
      <Route path="/iac-management" element={<ResourcesIacManagement />}>
        <Route path="resource/:resourceId" element={<IacManagementResourceDetails />} />
      </Route>

      {configurationManagementEnabled && (
        <Route path="/configuration-management" element={<ConfigManagement />} />
      )}

      {unmanagedResourcesUi && (
        <Route path="/scans" element={<ScansLayout />}>
          <Route index element={<Scans />} />
          <Route path=":scanId" element={<ScanDetailLayout />}>
            <Route index element={<Navigate to="resources" replace />} />
            <Route path="edit" element={<ScheduleScanWizard />} />
            <Route path="resources" element={<ScanResources />} />
            <Route path="scan-runs">
              <Route index element={<ScanDetailScans />} />
              <Route path=":runId" element={<ScanRunLayout />}>
                <Route index element={<Navigate to="resources" replace />} />
                <Route path="resources" element={<ScanResources />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      )}

      <Route path="*" element={<Navigate to="/resources/iac-management" replace />} />
    </Routes>
  );
};

export default ResourcesWrapper;
